import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import JourneyFormLayout from '../components/JourneyFormLayout';
import {
  fetchSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
  toggleSubject,
  exportSubjectsFile,
  importSubjectsFile,
} from '../services/subject';

const emptyForm = {
  name: '',
  code: '',
  description: '',
  color: '',
  icon: '',
  active: true,
};

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
const subjectFormSteps = ['Basics', 'Theme', 'Review'];

const slugify = (str = '') =>
  String(str)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const isHex = (value = '') => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value.trim());

const Subjects = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);

  const [modal, setModal] = useState({ open: false, mode: 'create', data: null, loading: false });
  const [modalStep, setModalStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, loading: false, title: '', message: '', action: null });
  const fileInputRef = useRef(null);

  const subjectStats = useMemo(() => {
    if (!Array.isArray(rows)) {
      return { total: 0, active: 0, inactive: 0 };
    }
    const activeCount = rows.filter((item) => item.active).length;
    const totalCount = rows.length;
    return {
      total: totalCount,
      active: activeCount,
      inactive: totalCount - activeCount,
    };
  }, [rows]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { q: search || undefined, sort: 'name' };
      if (statusFilter === 'active') params.active = true;
      if (statusFilter === 'inactive') params.active = false;
      const data = await fetchSubjects(params);
      const items = Array.isArray(data.items) ? data.items : [];
      setRows(items);
      setTotal(typeof data.total === 'number' ? data.total : items.length);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchAll, 200);
    return () => clearTimeout(timer);
  }, [fetchAll]);

  const resetForm = () => {
    setForm(emptyForm);
    setFormErrors({});
    setModalStep(0);
  };

  const openCreateModal = () => {
    resetForm();
    setModalStep(0);
    setModal({ open: true, mode: 'create', data: null, loading: false });
  };

  const openEditModal = (row) => {
    if (!row) return;
    setForm({
      name: row.name || '',
      code: row.code || '',
      description: row.description || '',
      color: row?.theme?.color || '',
      icon: row?.theme?.icon || '',
      active: Boolean(row.active),
    });
    setFormErrors({});
    setModalStep(0);
    setModal({ open: true, mode: 'edit', data: row, loading: false });
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.code.trim()) errors.code = 'Code is required';
    if (form.color && !isHex(form.color)) errors.color = 'Use a valid hex e.g. #0f172a';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateModalStep = (step) => {
    const errors = {};
    if (step === 0) {
      if (!form.name.trim()) errors.name = 'Name is required';
      if (!form.code.trim()) errors.code = 'Code is required';
    }
    if (step === 1 && form.color && !isHex(form.color)) {
      errors.color = 'Use a valid hex e.g. #0f172a';
    }
    if (Object.keys(errors).length) {
      setFormErrors((prev) => ({ ...prev, ...errors }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModal((prev) => ({ ...prev, loading: true }));

    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      theme: {
        color: form.color || undefined,
        icon: form.icon || undefined,
      },
      active: Boolean(form.active),
      slug: slugify(form.name),
    };

    try {
      if (modal.mode === 'create') {
        await createSubject(payload);
        setFeedback({ type: 'success', message: 'Subject created successfully.' });
      } else if (modal.data?._id) {
        await updateSubject(modal.data._id, payload);
        setFeedback({ type: 'success', message: 'Subject updated successfully.' });
      }
      setModal({ open: false, mode: 'create', data: null, loading: false });
      resetForm();
      fetchAll();
    } catch (err) {
      setModal((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.response?.data?.error || err?.message || 'Failed to save subject.' });
    }
  };

  const handleDelete = (row) => {
    if (!row?._id) return;
    setConfirmDialog({
      open: true,
      loading: false,
      title: 'Delete subject',
      message: `Delete ${row.name}? This action cannot be undone.`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteSubject(row._id);
          setFeedback({ type: 'success', message: 'Subject deleted.' });
          fetchAll();
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
        } catch (err) {
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          setFeedback({ type: 'error', message: err?.response?.data?.error || err?.message || 'Failed to delete subject.' });
        }
      },
    });
  };

  const handleToggle = async (row) => {
    if (!row?._id) return;
    try {
      const updated = await toggleSubject(row._id);
      setRows((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
      setFeedback({ type: 'success', message: `${updated.name} is now ${updated.active ? 'active' : 'inactive'}.` });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.response?.data?.error || err?.message || 'Failed to toggle subject.' });
    }
  };

  const handleExport = async (format) => {
    try {
      const blob = await exportSubjectsFile({ format, active: statusFilter === 'all' ? undefined : statusFilter === 'active' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `subjects.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setFeedback({ type: 'success', message: `Exported subjects as ${format.toUpperCase()}.` });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Export failed.' });
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const result = await importSubjectsFile(file);
      setFeedback({
        type: 'success',
        message: `Import complete. Created: ${result.created}, Updated: ${result.updated}, Skipped: ${result.skipped}`,
      });
      fetchAll();
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Import failed.' });
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openExportMenu = (event) => {
    setExportAnchor(event.currentTarget);
  };

  const closeExportMenu = () => {
    setExportAnchor(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Subject',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              {row.theme?.color && (
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: row.theme.color,
                  }}
                />
              )}
              <Typography variant="body1" fontWeight={600}>
                {row.name || 'Untitled Subject'}
              </Typography>
              {!row.active && <Chip label="Inactive" size="small" color="warning" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {row.code || '-'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.6,
        minWidth: 260,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {row.description || 'No description provided.'}
          </Typography>
        ),
      },
      {
        field: 'theme',
        headerName: 'Theme',
        width: 160,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">Color: {row.theme?.color || 'Default'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Icon: {row.theme?.icon || 'None'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 160,
        valueGetter: ({ value }) => (value ? new Date(value).toLocaleString() : '-'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton
            size="small"
            onClick={(event) => {
              setMenuAnchor(event.currentTarget);
              setMenuRow(row);
            }}
          >
            <MoreVertRoundedIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Tooltip title="Reload subjects">
            <span>
              <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={fetchAll}>
                Refresh
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or code"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              fullWidth
              size="small"
            />
            <AdminActionGroup align="end" sx={{ flexShrink: 0 }}>
              <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={openExportMenu}>
                Export
              </Button>
              <Tooltip title="Upload .csv or .json files">
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FileUploadRoundedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Import
                </Button>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,application/json,text/csv"
                style={{ display: 'none' }}
                onChange={handleImport}
              />
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateModal}>
                Add Subject
              </Button>
            </AdminActionGroup>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Tabs
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {STATUS_TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            >
              <Chip label={`Total: ${subjectStats.total || total}`} color="primary" variant="outlined" />
              <Chip label={`Active: ${subjectStats.active}`} color="success" variant="outlined" />
              <Chip label={`Inactive: ${subjectStats.inactive}`} color="warning" variant="outlined" />
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 0 }}>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row._id || row.id}
              disableRowSelectionOnClick
              loading={loading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              density="comfortable"
              sx={{ border: 0, '& .MuiDataGrid-cell': { alignItems: 'center' } }}
              slots={{
                noRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      No subjects yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {statusFilter === 'all'
                        ? 'Use the button above to add your first subject.'
                        : 'Try switching filters or clear your search term.'}
                    </Typography>
                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateModal}>
                      Add Subject
                    </Button>
                  </Stack>
                ),
                noResultsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      No matches
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Change your search or status filters to see subjects.
                    </Typography>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Paper>
      </Stack>

      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={closeExportMenu}>
        <MenuItem
          onClick={() => {
            closeExportMenu();
            handleExport('csv');
          }}
        >
          <FileDownloadRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Export CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeExportMenu();
            handleExport('json');
          }}
        >
          <FileDownloadRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Export JSON
        </MenuItem>
      </Menu>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            if (menuRow) openEditModal(menuRow);
          }}
        >
          <EditRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Edit subject
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            if (menuRow) handleToggle(menuRow);
          }}
        >
          <Switch size="small" checked={Boolean(menuRow?.active)} sx={{ mr: 1 }} />
          {menuRow?.active ? 'Mark inactive' : 'Mark active'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            if (menuRow) handleDelete(menuRow);
          }}
        >
          <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={modal.open}
        onClose={
          modal.loading
            ? undefined
            : () => {
                setModal({ open: false, mode: 'create', data: null, loading: false });
                setModalStep(0);
              }
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{modal.mode === 'edit' ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <JourneyFormLayout
              title={modal.mode === 'edit' ? 'Update Subject' : 'Create Subject'}
              subtitle="Use the same structured journey pattern as user forms."
              steps={subjectFormSteps}
              activeStep={modalStep}
              compact
              submitting={modal.loading}
              submitLabel={modal.mode === 'edit' ? 'Save changes' : 'Add subject'}
              onBack={() => setModalStep((prev) => Math.max(prev - 1, 0))}
              onNext={() => {
                if (!validateModalStep(modalStep)) return;
                setModalStep((prev) => Math.min(prev + 1, subjectFormSteps.length - 1));
              }}
              onSubmit={handleSave}
            >
              <Stack spacing={2}>
                {modalStep === 0 && (
                  <>
                    <TextField
                      label="Name"
                      value={form.name}
                      onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                      error={Boolean(formErrors.name)}
                      helperText={formErrors.name}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Code"
                      value={form.code}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          code: event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
                        }))
                      }
                      error={Boolean(formErrors.code)}
                      helperText={formErrors.code || 'Use letters/numbers only'}
                      fullWidth
                      required
                      inputProps={{ maxLength: 12 }}
                    />
                    <TextField
                      label="Description"
                      value={form.description}
                      onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </>
                )}

                {modalStep === 1 && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Theme color"
                          value={form.color}
                          onChange={(event) => setForm((prev) => ({ ...prev, color: event.target.value }))}
                          error={Boolean(formErrors.color)}
                          helperText={formErrors.color || 'Optional hex color e.g. #0f172a'}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Theme icon"
                          value={form.icon}
                          onChange={(event) => setForm((prev) => ({ ...prev, icon: event.target.value }))}
                          helperText="Optional icon name"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.active}
                          onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
                        />
                      }
                      label="Active"
                    />
                  </>
                )}

                {modalStep === 2 && (
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {form.name || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Code:</strong> {form.code || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Description:</strong> {form.description || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Theme color:</strong> {form.color || 'Default'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Theme icon:</strong> {form.icon || 'None'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {form.active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </JourneyFormLayout>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setModal({ open: false, mode: 'create', data: null, loading: false });
              setModalStep(0);
            }}
            disabled={modal.loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={confirmDialog.loading ? undefined : () => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}
            disabled={confirmDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (confirmDialog.action) {
                await confirmDialog.action();
              }
            }}
            disabled={confirmDialog.loading}
          >
            {confirmDialog.loading ? 'Working...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Subjects;
